import { createContext, useContext, useState, useCallback } from 'react';

const BuilderContext = createContext();

const SNAP_ORDER = ['peek', 'half', 'full'];

export function BuilderProvider({ children }) {
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [activePage, setActivePage] = useState(null);
  // The page the URL route is currently showing, independent of which block
  // (if any) is selected. Selection uses `activePage`; anything that needs
  // "the page the user is looking at right now" — the Outline tab — must use
  // this instead, or it silently falls back to whatever page was last
  // selected (or 'home') on routes where nothing has been tapped yet. Kept
  // in sync from App.js's location.pathname effect.
  const [currentPage, setCurrentPage] = useState('home');
  const [sidebarPanelOpen, setSidebarPanelOpen] = useState(false);
  const [sheetSnap, setSheetSnap] = useState('peek');
  const [isDirty, setIsDirty] = useState(false);

  const cycleSheetSnap = useCallback(() => {
    setSheetSnap((current) => {
      const next = (SNAP_ORDER.indexOf(current) + 1) % SNAP_ORDER.length;
      return SNAP_ORDER[next];
    });
  }, []);

  const selectBlock = useCallback((blockId, pageKey) => {
    setSelectedBlockId(blockId);
    setActivePage(pageKey);
    // Reveal the properties, but never shrink a sheet the user already opened.
    setSheetSnap((current) => (current === 'peek' ? 'half' : current));
  }, []);

  const deselectBlock = useCallback(() => {
    setSelectedBlockId(null);
    setActivePage(null);
    setSheetSnap('peek');
  }, []);

  const markDirty = useCallback(() => setIsDirty(true), []);
  const markClean = useCallback(() => setIsDirty(false), []);

  return (
    <BuilderContext.Provider value={{
      selectedBlockId,
      setSelectedBlockId,
      activePage,
      setActivePage,
      currentPage,
      setCurrentPage,
      sidebarPanelOpen,
      setSidebarPanelOpen,
      sheetSnap,
      setSheetSnap,
      cycleSheetSnap,
      isDirty,
      markDirty,
      markClean,
      selectBlock,
      deselectBlock,
    }}>
      {children}
    </BuilderContext.Provider>
  );
}

export function useBuilder() {
  return useContext(BuilderContext);
}
